import { Component, inject, OnInit, signal, computed, HostListener, LOCALE_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { StockService } from '../../../services/product/stock/stock';
import { SuppliersService } from '../../../services/suppliers/suppliers.service';
import { ProductService } from '../../../services/product/product.service';
import { StockFiltersComponent } from './stock-filters-component/stock-filters-component';
import { CurrentStockTableComponent } from './current-stock-table-component/current-stock-table-component';
import { HistoryStockTableComponent } from './history-stock-table-component/history-stock-table-component';
import {
  CurrentStockItem,
  StockFilters,
  HistoryStockFilters,
  ProductTitleResult,
  StockType,
} from '../../../pages/admin-dashboard/stock-management/stock.model';
import { AdminSuppliersList } from '../../../pages/admin-dashboard/admin-supplier/admin-supplier.model';
import { Loading } from '../../../utils/loading/loading';
import { ErrorData } from '../../../utils/error-data/error-data';
import { EmptyData } from '../../../utils/empty-data/empty-data';

@Component({
  selector: 'app-admin-stock-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    StockFiltersComponent,
    CurrentStockTableComponent,
    HistoryStockTableComponent,
    Loading,
    ErrorData,
    EmptyData,
  ],
  templateUrl: './admin-stock-list.html',
  styleUrls: ['./admin-stock-list.scss'],
})
export class AdminStockList implements OnInit {
  private stockService = inject(StockService);
  private suppliersService = inject(SuppliersService);
  private productService = inject(ProductService);
  private locale = inject(LOCALE_ID);

  loading = signal<boolean>(false);
  error = signal<boolean>(false);
  stockTypes = signal<StockType[]>([]);
  allStockData = signal<CurrentStockItem[]>([]);
  totalElements = signal<number>(0);
  currentPage = signal<number>(0);
  itemsPerPage = 25;

  suppliers = signal<AdminSuppliersList[]>([]);
  productSearchTerm = signal<string>('');
  isCurrentStockMode = signal<boolean>(true);
  showProductTitleDropdown = signal<boolean>(false);
  productTitleLoading = signal<boolean>(false);
  productTitleResults = signal<ProductTitleResult[]>([]);

  private productSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly noDataTitle = $localize`:@@stock.noRecords:No Stock Records Found`;
  readonly noDataSubTitle = $localize`:@@stock.tryAdjustingFilters:Try adjusting your filters or add a new stock entry.`;

  filters = signal<StockFilters>({
    productId: undefined,
    supplierId: undefined,
    expDateFrom: null,
    expDateTo: null,
    isActive: undefined,
    stockType: undefined,
    pageNum: 0,
    pageSize: 25,
    orderBy: 'LAST_MODIFIED_ON',
    orderDir: 'DESC',
  });

  @HostListener('document:click')
  clickout(): void {
    this.showProductTitleDropdown.set(false);
  }

  ngOnInit(): void {
    this.loadStockData();
    this.loadSuppliers();
    this.loadStockTypes();
  }

  loadStockData(): void {
    this.loading.set(true);
    this.error.set(false);

    const f = this.filters();

    if (this.isCurrentStockMode()) {
      const params: StockFilters = {
        pageNum: this.currentPage(),
        pageSize: this.itemsPerPage,
        orderBy: f.orderBy,
        orderDir: f.orderDir,
        productId: f.productId || undefined,
        supplierId: f.supplierId || undefined,
        isActive: f.isActive,
        expDateFrom: f.expDateFrom || null,
        expDateTo: f.expDateTo || null,
      };

      this.stockService.getCurrentStock(params).subscribe({
        next: (res) => {
          this.allStockData.set(res.list);
          this.totalElements.set(res.total);
          this.loading.set(false);
        },
        error: () => {
          this.allStockData.set([]);
          this.totalElements.set(0);
          this.loading.set(false);
          this.error.set(true);
        },
      });
    } else {
      const params: HistoryStockFilters = {
        pageNum: this.currentPage(),
        pageSize: this.itemsPerPage,
        orderBy: f.orderBy as HistoryStockFilters['orderBy'],
        orderDir: f.orderDir,
        productId: f.productId || undefined,
        supplierId: f.supplierId || undefined,
        typeId: f.typeId || undefined,
      };

      this.stockService.getHistoryStock(params).subscribe({
        next: (res) => {
          this.allStockData.set(res.list);
          this.totalElements.set(res.total);
          this.loading.set(false);
        },
        error: () => {
          this.allStockData.set([]);
          this.totalElements.set(0);
          this.loading.set(false);
          this.error.set(true);
        },
      });
    }
  }

  loadData(): void {
    this.loadStockData();
  }

  loadStockTypes(): void {
    this.stockService.getStockTypes().subscribe({
      next: (res) => this.stockTypes.set(res.list),
    });
  }

  loadSuppliers(): void {
    this.suppliersService.getAllSuppliers({ pageSize: 100, isActive: true }).subscribe({
      next: (res) => this.suppliers.set(res.list || []),
    });
  }

  getTranslatedTitle(item: ProductTitleResult): string {
    return this.locale.startsWith('ar') ? item.titleAr || item.titleEn : item.titleEn || item.titleAr;
  }

  onProductTitleFocus(): void {
    if (this.productSearchTerm().length >= 2) {
      this.showProductTitleDropdown.set(true);
      if (!this.productTitleResults().length) {
        this.searchProducts(this.productSearchTerm());
      }
    }
  }

  onSearchProduct(term: string): void {
    this.productSearchTerm.set(term);
    this.filters.update((f) => ({ ...f, productId: undefined }));
    if (this.productSearchTimeout) clearTimeout(this.productSearchTimeout);

    if (term.length < 2) {
      this.showProductTitleDropdown.set(false);
      this.productTitleResults.set([]);
      return;
    }

    this.showProductTitleDropdown.set(true);
    this.productTitleLoading.set(true);

    this.productSearchTimeout = setTimeout(() => {
      this.searchProducts(term);
    }, 300);
  }

  private searchProducts(term: string): void {
    this.productService.autocompleteAdminProducts(term).subscribe({
      next: (res) => {
        this.productTitleResults.set(
          res.list?.map((p) => ({
            id: p.id,
            titleAr: p.titleAr ?? '',
            titleEn: p.titleEn,
          })) ?? [],
        );
        this.productTitleLoading.set(false);
      },
      error: () => {
        this.productTitleResults.set([]);
        this.productTitleLoading.set(false);
      },
    });
  }

  onSelectProductTitle(product: ProductTitleResult): void {
    this.filters.update((f) => ({ ...f, productId: product.id }));
    this.productSearchTerm.set(this.getTranslatedTitle(product));
    this.showProductTitleDropdown.set(false);
    this.currentPage.set(0);
    this.loadStockData();
  }

  toggleStockMode(): void {
    this.isCurrentStockMode.update((val) => !val);
    this.resetFilters();
  }

  toggleSortOrder(): void {
    this.filters.update((f) => ({ ...f, orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC' }));
    this.loadStockData();
  }

  toggleOrder(): void {
    this.toggleSortOrder();
  }

  applyFilters(): void {
    this.currentPage.set(0);
    this.loadStockData();
  }

  resetFilters(): void {
    this.filters.set({
      productId: undefined,
      supplierId: undefined,
      expDateFrom: null,
      expDateTo: null,
      isActive: undefined,
      stockType: undefined,
      pageNum: 0,
      pageSize: 25,
      orderBy: this.isCurrentStockMode() ? 'LAST_MODIFIED_ON' : 'CREATION_DATE',
      orderDir: 'DESC',
    });
    this.productSearchTerm.set('');
    this.productTitleResults.set([]);
    this.showProductTitleDropdown.set(false);
    this.currentPage.set(0);
    this.loadStockData();
  }

  totalPages = computed(() => Math.ceil(this.totalElements() / this.itemsPerPage));
  startItem = computed(() => (this.totalElements() === 0 ? 0 : this.currentPage() * this.itemsPerPage + 1));

  previousPage(): void {
    if (this.currentPage() > 0) {
      this.currentPage.update((p) => p - 1);
      this.loadStockData();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages() - 1) {
      this.currentPage.update((p) => p + 1);
      this.loadStockData();
    }
  }

  goToPage(page: number): void {
    if (page !== -1) {
      this.currentPage.set(page - 1);
      this.loadStockData();
    }
  }

  getPageNumbers(): number[] {
    const total = this.totalPages();
    const current = this.currentPage() + 1;
    let pages: number[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 3) pages = [1, 2, 3, -1, total];
      else if (current >= total - 2) pages = [1, -1, total - 2, total - 1, total];
      else pages = [1, -1, current, -1, total];
    }
    return pages;
  }
  onPageSizeChange(size: number): void {
    this.itemsPerPage = size;
    this.filters.update((f) => ({ ...f, pageSize: size }));
    this.currentPage.set(0);
    this.loadStockData();
  }
}
